/**
 * Options Simulator tab of the Backtest page (owner answer 5, option A): BTC options on Delta
 * India replayed bar by bar with modelled fills and the adjustment engine. Runs are queued to
 * the server, which executes ONE at a time in a forked worker process; this tab shows live
 * progress (socket `optionsim:progress` / `optionsim:done`, with a polling fallback).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { TOKEN_KEY, WS_URL } from '../../utils/constants';
import { optionsimService, simError, SimCatalog, SimCoverage, SimRunRow } from '../../services/optionsim.service';
import SimSetupCard, { RunBody } from './SimSetupCard';
import SimRunsList, { LiveProgress } from './SimRunsList';
import SimResults from './SimResults';
import CycleReplay from './CycleReplay';

const inFlight = (r: SimRunRow | null) => !!r && (r.status === 'QUEUED' || r.status === 'RUNNING');

const OptionSimTab: React.FC = () => {
  const [catalog, setCatalog] = useState<SimCatalog | null>(null);
  const [coverage, setCoverage] = useState<SimCoverage | null>(null);
  const [runs, setRuns] = useState<SimRunRow[]>([]);
  const [selected, setSelected] = useState<SimRunRow | null>(null);
  const [loadingSel, setLoadingSel] = useState(false);
  const [live, setLive] = useState<LiveProgress | null>(null);
  const [starting, setStarting] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [replay, setReplay] = useState<{ key: string; cycle: number; label: string } | null>(null);
  const selectedId = useRef<number | null>(null);
  /** The results column: brought into view when a run is opened from further down the page. */
  const resultsRef = useRef<HTMLDivElement | null>(null);
  selectedId.current = selected?.id ?? null;

  const loadRuns = useCallback(async () => {
    try { setRuns(await optionsimService.listRuns()); } catch { /* keep the last list */ }
  }, []);

  const reveal = useCallback(() => window.setTimeout(() => {
    const el = resultsRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 64 || r.top > window.innerHeight - 160) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 60), []);

  const openRun = useCallback(async (id: number, show = false) => {
    setLoadingSel(true);
    try { setSelected(await optionsimService.getRun(id)); if (show) reveal(); } catch (e: any) { toast.error(`Could not load run: ${simError(e)}`); } finally { setLoadingSel(false); }
  }, [reveal]);

  useEffect(() => {
    optionsimService.strategies().then(setCatalog).catch(e => setLoadErr(simError(e)));
    optionsimService.coverage().then(setCoverage).catch(() => setCoverage(null));
    void loadRuns();
  }, [loadRuns]);

  // live progress over the app's socket (room user:<id>); polling below covers a missed event
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    let s: Socket | null = null;
    try {
      s = io(WS_URL, { auth: { token }, reconnection: true, forceNew: true });
      s.on('optionsim:progress', (d: any) => setLive({ id: Number(d?.id), pct: Number(d?.pct) || 0, phase: d?.phase ?? null }));
      s.on('optionsim:done', (d: any) => {
        setLive(null);
        void loadRuns();
        if (selectedId.current === Number(d?.id)) void openRun(Number(d.id));
        if (d?.status === 'DONE') toast.success('Simulation finished');
        else if (d?.status === 'FAILED') toast.error(`Simulation failed: ${d?.error || 'see the run'}`);
      });
    } catch { /* the polling fallback still works */ }
    return () => { s?.disconnect(); };
  }, [loadRuns, openRun]);

  useEffect(() => {
    if (!inFlight(selected)) return;
    const id = selected!.id;
    const t = setInterval(async () => {
      try {
        const r = await optionsimService.getRun(id);
        setSelected(cur => (cur && cur.id === id ? r : cur));
        if (!inFlight(r)) { setLive(null); void loadRuns(); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [selected?.id, selected?.status, loadRuns]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (body: RunBody) => {
    setStarting(true);
    try {
      const r = await optionsimService.createRun(body);
      toast.success('Simulation queued');
      await loadRuns();
      await openRun(r.id, true);
    } catch (e: any) {
      toast.error(simError(e));
    } finally { setStarting(false); }
  };

  const cancel = async (id: number) => {
    try { await optionsimService.cancel(id); toast('Stopping…'); } catch (e: any) { toast.error(simError(e)); }
  };
  const remove = async (id: number) => {
    try {
      await optionsimService.remove(id);
      if (selected?.id === id) setSelected(null);
      void loadRuns();
    } catch (e: any) { toast.error(simError(e)); }
  };

  const liveSel = selected && live && live.id === selected.id ? live : null;
  const showResults = selected && (selected.status === 'DONE' || selected.status === 'CANCELLED') && (selected.summary?.runs?.length ?? 0) > 0;

  return (
    <Box data-testid="optionsim-tab">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <CandlestickChartIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>Options Simulator</Typography>
        <Chip size="small" variant="outlined" label="BTC · Delta India · bar by bar · no look-ahead" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Replays BTC option strategies over stored Delta India MARK candles, with modelled bid/ask, depth, fees and margin, and the adjustment
        engine deciding at each step. Results are estimates: no historical order book exists. Nothing here can place an order.
      </Typography>
      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>Could not load the strategy catalogue: {loadErr}</Alert>}
      {coverage?.runner?.disabled && <Alert severity="warning" sx={{ mb: 2 }}>The simulation runner is switched off on this server (OPTIONSIM_RUNNER_DISABLE): runs will stay queued.</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <SimSetupCard catalog={catalog} coverage={coverage} busy={starting} onRun={start} />
          <SimRunsList runs={runs} selectedId={selected?.id ?? null} live={live} onOpen={id => { void openRun(id, true); }} onDelete={remove} onRefresh={loadRuns} />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9 }} ref={resultsRef} sx={{ scrollMarginTop: 72 }}>
          {!selected && !loadingSel && (
            <Card><CardContent>
              <Alert severity="info">
                Pick strategies on the left and press Run. Each strategy is replayed under base and stressed costs; the results show the
                comparison table, cumulative P&amp;L and drawdown, P&amp;L by cycle and path regime, and a replay of every basket with each fill and decision.
              </Alert>
            </CardContent></Card>
          )}
          {loadingSel && <LinearProgress sx={{ mb: 1 }} />}
          {selected && inFlight(selected) && (
            <Card data-testid="optionsim-progress">
              <CardContent>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'break-word', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>{selected.name || `Run ${selected.id}`}</Typography>
                  <Button size="small" color="error" startIcon={<StopIcon />} onClick={() => cancel(selected.id)} sx={{ flexShrink: 0 }}>Cancel</Button>
                </Stack>
                <LinearProgress variant={selected.status === 'QUEUED' && !liveSel ? 'indeterminate' : 'determinate'} value={liveSel?.pct ?? selected.progressPct} sx={{ height: 10, borderRadius: 1, mb: 1 }} />
                <Typography variant="body2">
                  {selected.status === 'QUEUED' && !liveSel ? 'Queued: runs start one at a time.' : `${liveSel?.phase || selected.phase || 'Running'} · ${liveSel?.pct ?? selected.progressPct}%`}
                </Typography>
                <Typography variant="caption" color="text.secondary">Runs in a separate server process with its own memory cap, so the live trading backend is not slowed.</Typography>
              </CardContent>
            </Card>
          )}
          {selected?.status === 'FAILED' && <Card><CardContent><Alert severity="error">Simulation failed: {selected.error}</Alert></CardContent></Card>}
          {selected?.status === 'CANCELLED' && !showResults && <Card><CardContent><Alert severity="warning">This run was cancelled before any strategy finished.</Alert></CardContent></Card>}
          {showResults && <SimResults key={selected!.id} run={selected!} onReplay={(key, cycle, label) => setReplay({ key, cycle, label })} />}
        </Grid>
      </Grid>

      {replay && selected && (
        <CycleReplay open onClose={() => setReplay(null)} runId={selected.id} keyId={replay.key} cycle={replay.cycle} label={replay.label} fx={selected.config?.usdInr ?? 85} />
      )}
    </Box>
  );
};

export default OptionSimTab;
